
import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { useSupabaseAuth } from '@/hooks/auth';

const RBACTester: React.FC = () => {
  const { user, hasAccess } = useSupabaseAuth();
  const [testResults, setTestResults] = useState<Record<string, boolean>>({});

  const roles = ['admin', 'staff', 'user'] as const;

  const testRole = (role: string) => {
    // Cast to appropriate type for hasAccess function
    const result = hasAccess(role as 'admin' | 'staff' | 'user');
    setTestResults(prev => ({ ...prev, [role]: result }));
  };

  const testAllRoles = () => {
    const results: Record<string, boolean> = {};
    roles.forEach(role => {
      results[role] = hasAccess(role);
    });
    setTestResults(results);
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>RBAC Access Tester</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div>
          <p className="text-sm text-muted-foreground mb-2">
            Current User: {user?.email || 'Not logged in'}
          </p>
        </div>

        <div className="space-y-2">
          <Button onClick={testAllRoles} className="w-full">
            Test All Roles
          </Button>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
            {roles.map(role => (
              <Button
                key={role}
                variant="outline"
                onClick={() => testRole(role)}
                className="capitalize"
              >
                Test {role}
              </Button>
            ))}
          </div>
        </div>

        <div className="space-y-2">
          <h4 className="font-medium">Test Results:</h4>
          {Object.entries(testResults).map(([role, hasAccess]) => (
            <div key={role} className="flex items-center justify-between">
              <span className="capitalize">{role}:</span>
              <Badge variant={hasAccess ? 'default' : 'destructive'}>
                {hasAccess ? 'Access Granted' : 'Access Denied'}
              </Badge>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
};

export default RBACTester;
